import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from "react-google-recaptcha";
import '../../../assets/styles/Auth.css';
import Navbar from "../../../components/AuctionFeatureComponents/Home/Navbar";
import { BACKEND_DOMAIN } from '../../../common/env';

const Register = () => {
  const [step, setStep] = useState(1); // 1: Registration form, 2: Email verification
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    gender: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: '',
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [captchaValue, setCaptchaValue] = useState(null);
  const [registrationResponse, setRegistrationResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(''); // For API error messages
  const [successMessage, setSuccessMessage] = useState(''); // For success messages

  const navigate = useNavigate(); // For redirecting after successful registration

  // Validation functions
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return re.test(password);
  };

  const validateUsername = (username) => {
    // At least 3 characters, alphanumeric and underscores only
    const re = /^[a-zA-Z0-9_]{3,}$/;
    return re.test(username);
  };

  const validateDateOfBirth = (date) => {
    if (!date) return false;
    const today = new Date();
    const birthDate = new Date(date);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Must be at least 13 years old
    if (age > 13) return true;
    if (age === 13 && monthDiff >= 0) return true;
    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear API error when user starts typing
    if (apiError) {
      setApiError('');
    }
  };

  const handleVerificationCodeChange = (e) => {
    setVerificationCode(e.target.value);
    
    // Clear API error when user starts typing
    if (apiError) {
      setApiError('');
    }
  };

  const handleCaptchaChange = (value) => {
    console.log("Captcha value:", value);
    setCaptchaValue(value);
    
    // Clear CAPTCHA error when user completes it
    if (errors.captcha) {
      setErrors(prev => ({ ...prev, captcha: '' }));
    }
    
    // Clear API error when user completes CAPTCHA
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!validateUsername(formData.username)) {
      newErrors.username = 'Username must be at least 3 characters and contain only letters, numbers, and underscores';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = 'Please select your gender';
    }

    // Date of birth validation
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else if (!validateDateOfBirth(formData.dateOfBirth)) {
      newErrors.dateOfBirth = 'You must be at least 13 years old';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // CAPTCHA validation
    if (!captchaValue) {
      newErrors.captcha = 'Please complete the CAPTCHA verification';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setApiError('');
    setSuccessMessage('');

    try {
      // Include CAPTCHA value in the registration data
      const registrationData = {
        ...formData,
        captcha: captchaValue
      };
      
      const response = await axios.post(`${BACKEND_DOMAIN}/register`, registrationData);
      console.log('Registration Success:', response.data);
      setRegistrationResponse(response.data);
      setSuccessMessage(response.data.message);
      
      // Check if email verification is required
      if (response.data.emailVerificationRequired !== false) {
        setStep(2); // Move to verification step
      } else {
        // If email verification is not configured, redirect to login after a delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error) {
      console.error('Registration Error:', error);
      if (error.response && error.response.data && error.response.data.message) {
        setApiError(error.response.data.message);
      } else {
        setApiError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    
    if (!verificationCode.trim()) {
      setApiError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setApiError('');
    setSuccessMessage('');

    try {
      const response = await axios.post(`${BACKEND_DOMAIN}/verify-email`, {
        email: formData.email,
        verificationCode: verificationCode
      });
      
      console.log('Verification Success:', response.data);
      setSuccessMessage('Email verified successfully! Redirecting to login...');
      
      // Redirect to login after successful verification
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Verification Error:', error);
      if (error.response && error.response.data && error.response.data.message) {
        setApiError(error.response.data.message);
      } else {
        setApiError('Verification failed. Please check the code and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setApiError('');
    setSuccessMessage('');

    try {
      const response = await axios.post(`${BACKEND_DOMAIN}/resend-verification`, {
        email: formData.email
      });
      console.log('Resend Success:', response.data);
      setSuccessMessage('Verification code resent to your email!');
    } catch (error) {
      console.error('Resend Error:', error);
      if (error.response && error.response.data && error.response.data.message) {
        setApiError(error.response.data.message);
      } else {
        setApiError('Failed to resend verification code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
    
    <div className="auth-container" style={{ marginTop: "80px" }}>
      <div className="auth-card">
        {step === 1 ? (
          <>
            <h2 className="auth-title">Create Your Account</h2>
            {apiError && <div className="alert alert-danger">{apiError}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="username" className="form-label">Username</label>
                <input
                  type="text"
                  className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter your username"
                />
                {errors.username && <div className="invalid-feedback">{errors.username}</div>}
              </div>
              
              <div className="mb-3">
                <label className="form-label">Gender</label>
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="Male"
                    name="gender"
                    value="Male"
                    checked={formData.gender === 'Male'}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="Male">
                    Male
                  </label>
                </div>
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="Female"
                    name="gender"
                    value="Female"
                    checked={formData.gender === 'Female'}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="Female">
                    Female
                  </label>
                </div>
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="other"
                    name="gender"
                    value="Other"
                    checked={formData.gender === 'Other'}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="Other">
                    Other
                  </label>
                </div>
                {errors.gender && <div className="invalid-feedback d-block">{errors.gender}</div>}
              </div>
              
              <div className="mb-3">
                <label htmlFor="dateOfBirth" className="form-label">Date of Birth</label>
                <input
                  type="date"
                  className={`form-control ${errors.dateOfBirth ? 'is-invalid' : ''}`}
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                />
                {errors.dateOfBirth && <div className="invalid-feedback">{errors.dateOfBirth}</div>}
              </div>
              
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input
                  type="email"
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>
              
              <div className="mb-3">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                />
                {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                <div className="form-text">
                  Password must be at least 8 characters with uppercase, lowercase, and number
                </div>
              </div>
              
              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                <input
                  type="password"
                  className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                />
                {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
              </div>
              
              {/* CAPTCHA */}
              <div className="mb-3 recaptcha-container">
                <ReCAPTCHA
                  sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" // Test key - replace with your actual site key
                  onChange={handleCaptchaChange}
                />
                {errors.captcha && <div className="invalid-feedback d-block text-center mt-2">{errors.captcha}</div>}
              </div>
              
              <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                {loading ? 'Registering...' : 'Sign Up'}
              </button>
            </form>
            <p className="auth-footer">
              Already have an account? <Link to="/login">Login</Link>
            </p>
          </>
        ) : (
          <>
            <h2 className="auth-title">Verify Your Email</h2>
            <p className="text-center mb-4">
              We've sent a verification code to <strong>{formData.email}</strong>. 
              Please enter the code below to verify your email address.
            </p>
            <p className="text-center mb-4 text-warning">
              <strong>Note:</strong> If you don't receive an email, please check your spam folder or contact the administrator.
            </p>
            {apiError && <div className="alert alert-danger">{apiError}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}
            <form onSubmit={handleVerificationSubmit}>
              <div className="mb-3">
                <label htmlFor="verificationCode" className="form-label">Verification Code</label>
                <input
                  type="text"
                  className="form-control"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={handleVerificationCodeChange}
                  placeholder="Enter verification code"
                  maxLength="6"
                />
              </div>
              <button type="submit" className="btn btn-primary w-100 mb-3" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify Email'}
              </button>
            </form>
            <div className="d-flex justify-content-between">
              <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>
                Back to Registration
              </button>
              <button className="btn btn-link" onClick={handleResendCode} disabled={loading}>
                {loading ? 'Resending...' : 'Resend Code'}
              </button>
            </div>
            <p className="auth-footer mt-3">
              Already have an account? <Link to="/login">Login</Link>
            </p>
          </>
        )}
      </div>
    </div>
    </div>
  );
};

export default Register;